import axios from 'axios';
import { Buffer } from 'buffer';
import { chunk } from 'lodash';
class RayKeyClient {
    #apiDomain;
    #apiUri;
    #publicKey;
    #_axios;
	constructor(initialApiDomain, initialApiUri, initialPublicKey) {
        this.#apiDomain = initialApiDomain;
        this.#apiUri = initialApiUri;
        if (initialPublicKey) {
            this.#publicKey = this.#getBase64PublicKey(initialPublicKey);
        }
        this.#_axios = axios.create({
            baseURL: this.#apiDomain,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Network error retry interceptor (mirrors global axiosSetup.js logic)
        // axios.create() instances are not covered by global interceptors.
        // Related: BUG-038 — RayKeyClient AxiosError Network Error unhandled
        this.#_axios.interceptors.response.use(
            (response) => response,
            async (retryError) => {
                const cfg = retryError.config;
                if (!cfg) return Promise.reject(retryError);

                const isNetworkErr = !retryError.response && retryError.message === 'Network Error';
                const isTimeout = retryError.code === 'ECONNABORTED';
                cfg.__retryCount = cfg.__retryCount || 0;

                if ((isNetworkErr || isTimeout) && cfg.__retryCount < 1) {
                    cfg.__retryCount += 1;
                    await new Promise((resolve) => setTimeout(resolve, 1500));
                    return this.#_axios(cfg);
                }

                if (isNetworkErr || isTimeout) {
                    console.warn(
                        '[RayKeyClient] Network Error — request failed without response.',
                        'URL:', cfg.url,
                        'Method:', cfg.method
                    );
                }

                return Promise.reject(retryError);
            }
        );
	}
    #getBase64PublicKey(pem) {
        const b64 = pem.replace(/-----BEGIN PUBLIC KEY-----/g, '')
            .replace(/-----END PUBLIC KEY-----/g, '')
            .replace(/\n/g, '');
        return b64.trim();
    }
    async getPublicKey() {
        const apiUrl = this.#apiDomain + this.#apiUri;
        const payload = { pk: Date.now() };
        const ret = await this.#_axios.post(apiUrl, payload);
        this.#publicKey = this.#getBase64PublicKey(ret.data.data);
        return ret.data.data;
    }
    #generateRandToken() {
        return Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => ('0' + b.toString(16)).slice(-2)).join('');
    }
    async #generatePayload(originPayload, _publickey) {
        const inputText = JSON.stringify(originPayload);
        let texts = chunk(inputText, 150);
        texts = texts.map(x => x.join(""));
        const publicKey = await this.#importPublicKey(_publickey || this.#publicKey);
        const payloadData = [];
        for (const text of texts) {
            const encryptText = await this.#encryptMessage(text, publicKey);
            payloadData.push(encryptText);
        }
        const payload = {
            data: payloadData,
        };
        return payload;
    }
    async #importPublicKey(publicKeyBase64) {
        const publicKeyBuffer = Uint8Array.from(Buffer.from(publicKeyBase64, "base64"));
        const publicKey = await window.crypto.subtle.importKey(
            "spki",
            publicKeyBuffer,
            {
                name: "RSA-OAEP",
                hash: "SHA-256"
            },
            true,
            ["encrypt"]
        );
        return publicKey;
    }
    async #encryptMessage(inputText, key) {
        const enc = new TextEncoder();
        const encoded = enc.encode(inputText);
        const cipherText = await window.crypto.subtle.encrypt(
            {
                name: "RSA-OAEP"
            },
            key,
            encoded
        );
        const buffer = new Uint8Array(cipherText);
        const hex = Buffer.from(buffer).toString('hex');
        return hex;
    }
    async CallApi(data) {
        if (!this.#publicKey) {
            await this.getPublicKey();
        }
        const apiUrl = this.#apiDomain + this.#apiUri;
        const randToken = this.#generateRandToken();
        const payload = {
            requestTimeMs: Date.now(),
            data: data,
            token: randToken,
        };
        const encodedPayload = await this.#generatePayload(payload, this.#publicKey);
        const axiosConfig = {
            headers: {
                "x-rt-security": (await this.#generatePayload(randToken, this.#publicKey)).data[0],
            }
        };
        const ret = await this.#_axios.post(apiUrl, encodedPayload, axiosConfig);
        if (ret.data.status === "success") {
            return ret.data.data;
        }
        if (ret.data.iv) {
            const decrypted = await this.#decryptMessage(ret.data.encryptedData, ret.data.iv, randToken);
            return JSON.parse(decrypted)?.data ?? JSON.parse(decrypted);
        }
        return null;
    }
    async #decryptMessage(encryptedData, ivHex, secretKey) {
        const algorithm = { name: 'AES-CBC' };
        const key = await window.crypto.subtle.importKey(
            'raw',
            new Uint8Array(secretKey.match(/.{1,2}/g).map(byte => parseInt(byte, 16))), // 16진수 문자열을 Uint8Array로 변환
            algorithm,
            false,
            ['decrypt']
        );
        const iv = new Uint8Array(ivHex.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        const encryptedArray = new Uint8Array(encryptedData.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        const decrypted = await window.crypto.subtle.decrypt(
            { name: 'AES-CBC', iv: iv },
            key,
            encryptedArray
        );
        return new TextDecoder().decode(decrypted);
    }
}
export { RayKeyClient };

